import { useMemo } from "react"
import { Link } from "react-router-dom"
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import { H3, H5 } from "~/ui-rtk/components/ui/typography"
import ChartTooltipContent from "~/ui-rtk/components/ui/charts/ComposedChart/ChartTooltipContent"
import { type TLabelConfig } from "~/ui-rtk/constants/charts"
import { SOURCES_MAP } from "~/ui-rtk/constants/sources"
import { getColorFromPaletteByIdx } from "~/ui-rtk/utils/chart-utils"
import { formatNumber } from "~/ui-rtk/utils/format"
import useTailwindTheme from "~/ui-rtk/hooks/tailwind-theme"
import useDate from "~/ui-rtk/hooks/date"
import { ArrowTopRightOnSquare } from "~/ui-rtk/components/ui/svg/arrow/ArrowTopRightOnSquare"
import {
  toCoef,
  toCurrency,
  toNumber,
  toPercentage,
} from "~/ui-rtk/utils/number-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import {
  BRAND_METRICS,
  COEF_METRICS,
  CREATIVE_REPORTS_METRICS_MAP,
  ENGAGEMENT_METRICS,
  MONETARY_METRICS,
  PERCENTAGE_METRICS,
  PERFORMANCE_METRICS,
  REVENUE_METRICS,
  type TMetricsMapKey,
} from "../../connect"
import {
  getFacebookEmbedUrl,
  getPublicPostForRow,
  parseArrayString,
} from "../../utils"
import { useGetMetaPostPreviewQuery } from "~/ui-rtk/api/paidMediaApi"
import useCreativeTrendQuery from "./hooks/cube-query"

export type TCreativeDetailsProps = {
  metric: Record<string, string>
  title: string
  primaryIdKey: string
  primaryIdLabel?: string
  isAggregated?: boolean
  imgUrl?: string
  /** AMS-resolved media type; drives playable video vs public-post fallback */
  assetMediaType?: "IMAGE" | "VIDEO"
  metricsNames?: TMetricsMapKey[]
  dateRange: [string, string]
}

// Metadata / array keys that are rendered as dedicated sections, not metric rows
const NON_ROW_METRICS: TMetricsMapKey[] = [
  "md_image_url",
  "media_permalink_url",
  "all_ad_names",
  "all_adset_names",
  "all_campaign_names",
]

const SPEND_COLOR = getColorFromPaletteByIdx(0)
const BRAND_VALUE_COLOR = getColorFromPaletteByIdx(1)
const PURCHASES_COLOR = getColorFromPaletteByIdx(2)

const TREND_DATA_LABELS = new Map<string, TLabelConfig>([
  [
    "spend",
    {
      label: "Spend",
      numberType: "currency",
      dataKey: "spend",
      pluralizeUnit: false,
    },
  ],
  [
    "brandValue",
    {
      label: "Brand Value",
      numberType: "currency",
      dataKey: "brandValue",
      pluralizeUnit: false,
    },
  ],
  [
    "purchases",
    {
      label: "Purchases",
      dataKey: "purchases",
      pluralizeUnit: false,
    },
  ],
])

const METRIC_SECTIONS: { label: string; metrics: TMetricsMapKey[] }[] = [
  { label: "Brand Metrics", metrics: BRAND_METRICS },
  { label: "Revenue Metrics", metrics: REVENUE_METRICS },
  { label: "Platform Engagements", metrics: ENGAGEMENT_METRICS },
  { label: "Performance Metrics", metrics: PERFORMANCE_METRICS },
]

const formatMetricValue = (metric: TMetricsMapKey, value: string) => {
  if (MONETARY_METRICS.includes(metric)) return toCurrency(value)
  if (COEF_METRICS.includes(metric)) return toCoef(value)
  if (PERCENTAGE_METRICS.includes(metric)) return toPercentage(value)
  return toNumber(value)
}

export default function CreativeDetails({
  metric,
  title,
  primaryIdKey,
  primaryIdLabel,
  isAggregated = false,
  imgUrl,
  assetMediaType,
  metricsNames = [],
  dateRange,
}: TCreativeDetailsProps) {
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const theme = useTailwindTheme()
  const dayjs = useDate()

  // In aggregated mode the card spans all channels, so the trend is filtered
  // only by the primary id; in grouped mode it is scoped to the card's channel.
  const { trend, isLoading, error } = useCreativeTrendQuery({
    primaryIdKey,
    primaryIdValue: metric[primaryIdKey] ?? "",
    mediaChannel: isAggregated ? undefined : metric.media_channel,
    dateRange,
  })

  const mediaChannels = useMemo(() => {
    const rawChannels = isAggregated
      ? (parseArrayString(metric.all_media_channels) ?? [])
      : [metric.media_channel].filter((channel): channel is string => !!channel)
    return rawChannels
      .map(channel => channel.toLowerCase())
      .filter((channel, idx, arr) => arr.indexOf(channel) === idx)
  }, [metric, isAggregated])

  const iconChannels = mediaChannels.filter(
    channel => !!SOURCES_MAP[channel.toUpperCase() as keyof typeof SOURCES_MAP],
  )

  const servingCampaigns = parseArrayString(metric.all_campaign_names)
  const servingAdSets = parseArrayString(metric.all_adset_names)

  // Public post fallback: when the creative's video couldn't be downloaded
  // (whitelisted/collab creator-page ads, dropped/black thumbnails), the ad's
  // public FB/IG post is still playable — embed it instead of a dead still.
  // Resolved from the fields of the same ad (see getPublicPostForRow): in
  // aggregated mode that is the top-spend Meta row, never the group-wide MAX.
  const rowPublicPost = useMemo(
    () => getPublicPostForRow(metric, isAggregated),
    [metric, isAggregated],
  )

  // Facebook ad posts are usually unpublished ("dark") posts. The embedded-post
  // plugin can't render those for an anonymous viewer and silently falls back
  // to other content from the Page, so ask the API for the post's canonical
  // permalink and published state and only embed when it is published.
  const fbObjectStoryId =
    rowPublicPost?.platform === "Facebook"
      ? rowPublicPost.objectStoryId
      : undefined
  const {
    data: fbPreview,
    isFetching: isFbPreviewFetching,
    isError: isFbPreviewError,
  } = useGetMetaPostPreviewQuery(
    { objectStoryId: fbObjectStoryId ?? "" },
    { skip: !fbObjectStoryId },
  )

  // The section (with the "View on …" link) is always shown when the ad has a
  // post reference; only the embed depends on the lookup. Instagram posts and
  // Facebook permalinks embed directly. A Facebook story id embeds only once
  // Graph confirms the post is published — never a guessed post.
  const publicPost = useMemo(() => {
    if (!rowPublicPost || !fbObjectStoryId) return rowPublicPost
    const postUrl = fbPreview?.permalinkUrl ?? rowPublicPost.postUrl
    return {
      ...rowPublicPost,
      postUrl,
      embedUrl: getFacebookEmbedUrl(postUrl),
      isEmbeddable: fbPreview?.isPublished === true,
    }
  }, [rowPublicPost, fbObjectStoryId, fbPreview])
  const showPostEmbed = !!publicPost && assetMediaType !== "VIDEO"
  let fbPreviewNote =
    "No public preview is available for this post (unpublished ad post). Use the link above to open it."
  if (isFbPreviewFetching) {
    fbPreviewNote = "Checking whether Facebook offers a public preview…"
  } else if (isFbPreviewError) {
    fbPreviewNote =
      "Could not check this post with Facebook. Use the link above to open it."
  }

  // All metrics currently selected as columns on the page, always surfaced at
  // the top of the drawer next to the creative image.
  const keyMetrics = useMemo(
    () =>
      metricsNames
        .filter(
          key => !NON_ROW_METRICS.includes(key) && metric[key] !== undefined,
        )
        .map(key => ({
          key,
          label: CREATIVE_REPORTS_METRICS_MAP[key],
          value: formatMetricValue(key, metric[key]),
        })),
    [metric, metricsNames],
  )

  const sections = useMemo(
    () =>
      METRIC_SECTIONS.map(({ label, metrics }) => ({
        label,
        rows: metrics
          .filter(
            key => !NON_ROW_METRICS.includes(key) && metric[key] !== undefined,
          )
          .map(key => ({
            key,
            label: CREATIVE_REPORTS_METRICS_MAP[key],
            value: formatMetricValue(key, metric[key]),
            selected: metricsNames.includes(key),
          })),
      })).filter(section => section.rows.length > 0),
    [metric, metricsNames],
  )

  const renderNamesSection = (label: string, names: string[] | null) => {
    if (!names || names.length === 0) return null
    return (
      <Card
        rounded
        className="mt-3 bg-basic-dark-blue border border-basic-blue"
      >
        <H5 className="font-bold mb-2">{label}</H5>
        <div className="flex flex-wrap gap-2">
          {names.map((name, idx) => (
            <span
              key={`${name}-${idx}`}
              className={cn(
                "inline-block px-3 py-1 rounded-full text-xs text-white font-medium bg-system-blue-600 whitespace-nowrap",
                isDemoEnabled && "blur-md",
              )}
              title={name}
            >
              {name}
            </span>
          ))}
        </div>
      </Card>
    )
  }

  return (
    <div className="creative-details w-full h-full overflow-y-auto scrollbar pr-2">
      {/* Full ad name, wrapping instead of truncating */}
      <H3
        className={cn("font-bold break-words mb-1", isDemoEnabled && "blur-md")}
      >
        {title}
      </H3>
      {primaryIdLabel && (
        <div className="text-sm text-gray-light mb-3">
          Report on: {primaryIdLabel}
        </div>
      )}

      {/* Header: creative image + channels + key metrics at a glance */}
      <Card rounded className="bg-basic-dark-blue border border-basic-blue p-4">
        <div className="flex gap-4">
          <div
            className={cn(
              "w-44 h-44 flex-shrink-0 rounded-lg overflow-hidden bg-basic-black/50 border border-basic-blue/40",
              isDemoEnabled && "blur-md",
            )}
          >
            <Image
              src={imgUrl ?? metric.image_url}
              fallback={metric.image_url ?? metric.thumbnail_url}
              alt={title}
              mediaType={assetMediaType === "VIDEO" ? "video" : undefined}
              classes={{
                wrapper: "w-full h-full",
                image: "w-full h-full object-cover",
              }}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-0 gap-2">
            {(iconChannels.length > 0 || metric.creative_url) && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {iconChannels.map(channel => {
                    const source =
                      SOURCES_MAP[
                        channel.toUpperCase() as keyof typeof SOURCES_MAP
                      ]
                    return (
                      <img
                        key={channel}
                        src={source.icon}
                        alt={source.name}
                        title={source.name}
                        className="h-6 w-6 object-contain"
                      />
                    )
                  })}
                </div>
                {metric.creative_url && (
                  <Link
                    to={metric.creative_url}
                    target="_blank"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-basic-dark-blue border border-basic-blue rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer transition-all hover:bg-basic-blue active:scale-95"
                  >
                    <ArrowTopRightOnSquare fill="white" size={16} /> View Ad
                  </Link>
                )}
              </div>
            )}
            {keyMetrics.length > 0 && (
              <div className="grid grid-cols-2 gap-2 content-start">
                {keyMetrics.map(({ key, label, value }) => (
                  <div
                    key={key}
                    className="rounded-lg border border-basic-blue/40 bg-basic-black/40 px-3 py-1.5"
                  >
                    <div
                      className="text-xs text-gray-200 font-medium truncate"
                      title={label}
                    >
                      {label}
                    </div>
                    <div className="text-sm font-bold text-white">{value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Public post embed — the playable fallback when we don't have the
          video itself (whitelisted/collab ads, broken thumbnails) */}
      {showPostEmbed && publicPost && (
        <Card
          rounded
          className="mt-3 bg-basic-dark-blue border border-basic-blue"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <H5 className="font-bold">Public Post</H5>
            <Link
              to={publicPost.postUrl}
              target="_blank"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-basic-dark-blue border border-basic-blue rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer transition-all hover:bg-basic-blue active:scale-95"
            >
              <ArrowTopRightOnSquare fill="white" size={16} /> View on{" "}
              {publicPost.platform}
            </Link>
          </div>
          {"isEmbeddable" in publicPost && !publicPost.isEmbeddable ? (
            <div className="text-sm text-gray-200">{fbPreviewNote}</div>
          ) : (
            <iframe
              src={publicPost.embedUrl}
              title={`Public post for ${title}`}
              loading="lazy"
              allow="encrypted-media"
              className={cn(
                "w-full max-w-[400px] h-[480px] mx-auto rounded-lg border-0 bg-white",
                isDemoEnabled && "blur-md",
              )}
            />
          )}
        </Card>
      )}

      {/* Daily trend */}
      <Card
        rounded
        className="mt-3 bg-basic-dark-blue border border-basic-blue"
      >
        <div className="flex items-center justify-between gap-2">
          <H5 className="font-bold">Daily Performance</H5>
          <div className="flex items-center gap-4 text-sm text-gray-200">
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-1 rounded"
                style={{ backgroundColor: SPEND_COLOR }}
              />
              Spend
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-1 rounded"
                style={{ backgroundColor: BRAND_VALUE_COLOR }}
              />
              Brand Value
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-1 rounded"
                style={{ backgroundColor: PURCHASES_COLOR }}
              />
              Purchases
            </div>
          </div>
        </div>
        {error && (
          <div className="text-sm text-error mt-2">
            Failed to load trend data
          </div>
        )}
        {!error && isLoading && trend.length === 0 && (
          <div className="h-40 flex items-center justify-center">
            <Loader size={32} />
          </div>
        )}
        {!error && !(isLoading && trend.length === 0) && (
          <div className="h-56 mt-2 relative">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-basic-black/40 rounded-lg">
                <Loader size={24} />
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={trend}
                margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 0"
                  vertical={false}
                  stroke={theme.colors["gray-dark"]}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={value => dayjs(value).format("DD MMM")}
                  interval="preserveStart"
                  minTickGap={30}
                  className="text-sm"
                />
                <YAxis
                  domain={[0, "auto"]}
                  orientation="right"
                  interval="preserveEnd"
                  width={0.01}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value, index) =>
                    index !== 0 || value > 0
                      ? `${formatNumber(value, { compact: true })}`
                      : ""
                  }
                />
                <Area
                  type="monotone"
                  dataKey="spend"
                  strokeWidth={2}
                  stroke={SPEND_COLOR}
                  fill={SPEND_COLOR}
                  fillOpacity={0.25}
                  animationDuration={0}
                />
                <Line
                  type="monotone"
                  dataKey="brandValue"
                  strokeWidth={2}
                  stroke={BRAND_VALUE_COLOR}
                  fill={BRAND_VALUE_COLOR}
                  dot={{ r: 1 }}
                  animationDuration={0}
                />
                {/* Purchase counts are far smaller than dollar values, so they
                    get their own hidden axis to stay readable. */}
                <YAxis yAxisId="purchases" domain={[0, "auto"]} hide />
                <Line
                  type="monotone"
                  yAxisId="purchases"
                  dataKey="purchases"
                  strokeWidth={2}
                  stroke={PURCHASES_COLOR}
                  fill={PURCHASES_COLOR}
                  dot={{ r: 1 }}
                  animationDuration={0}
                />
                <Tooltip
                  animationDuration={0}
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) {
                      return null
                    }
                    return (
                      <ChartTooltipContent
                        payload={payload}
                        dataLabels={TREND_DATA_LABELS}
                        showTotal={false}
                        hideTooltipTitle
                      />
                    )
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Metrics grouped by category */}
      {sections.map(section => (
        <Card
          key={section.label}
          rounded
          className="mt-3 bg-basic-dark-blue border border-basic-blue"
        >
          <H5 className="font-bold mb-2">{section.label}</H5>
          <div className="grid grid-cols-3 gap-2">
            {section.rows.map(row => (
              <div
                key={row.key}
                className={cn(
                  "rounded-lg border border-basic-blue/40 bg-basic-black/40 px-3 py-2",
                  row.selected && "border-brand/60",
                )}
              >
                <div
                  className="text-xs text-gray-200 font-medium truncate"
                  title={row.label}
                >
                  {row.label}
                </div>
                <div className="text-base font-bold text-white mt-0.5">
                  {row.value}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {renderNamesSection("Serving Campaigns", servingCampaigns)}
      {renderNamesSection("Serving Ad Sets", servingAdSets)}
    </div>
  )
}
